import { useRef, useCallback } from 'react';
import { Show } from '../../types/Show';
import { debugError, debugLog, debugWarn } from '../../utils/debugLog';

/**
 * Configuration options for show detection behavior
 */
export interface ShowDetectionConfig {
  /** Whether to focus only on today's shows for detection (default: true) */
  todayOnly?: boolean;
  /** Custom date format for comparison (default: uses date_start_time) */
  dateField?: keyof Show;
  /** Custom show identifier field (default: uses show + date_start_time) */
  idFields?: (keyof Show)[];
  /** Whether to enable debug logging (default: false) */
  enableLogging?: boolean;
}

/**
 * Information about detected new shows
 */
export interface DetectionResult {
  /** All newly detected shows */
  newShows: Show[];
  /** New shows from today only */
  todayNewShows: Show[];
  /** The most recent new show (for animation purposes) */
  newestShow: Show | null;
  /** Total count of new shows detected */
  totalCount: number;
  /** Whether any new shows were detected */
  hasNewShows: boolean;
}

/**
 * Return type for the useShowDetection hook
 */
export interface UseShowDetectionReturn {
  /** Detect new shows by comparing current with previous */
  detectNewShows: (currentShows: Show[]) => DetectionResult;
  /** Update the baseline for comparison (call after processing new shows) */
  updateBaseline: (shows: Show[]) => void;
  /** Get the current baseline shows for comparison */
  getBaseline: () => Show[];
  /** Clear the baseline (useful for reset scenarios) */
  clearBaseline: () => void;
  /** Get statistics about detection history */
  getStats: () => {
    totalDetections: number;
    lastDetectionTime: number | null;
    baselineSize: number;
  };
}

/**
 * Utility functions for show comparison and identification
 */
class ShowDetectionUtils {
  /**
   * Generate a unique identifier for a show
   */
  static generateShowId(show: Show, idFields: (keyof Show)[] = ['show', 'date_start_time']): string {
    return idFields
      .map(field => String(show[field] || ''))
      .filter(value => value.length > 0)
      .join('-');
  }

  /**
   * Check if a show is from today
   */
  static isShowFromToday(show: Show): boolean {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const showDate = show.date_start_time?.split('#')[0] || '';
      return showDate === today;
    } catch (error) {
      debugWarn('Error checking if show is from today:', error);
      return false;
    }
  }

  /**
   * Filter shows to only include today's shows
   */
  static filterTodayShows(shows: Show[]): Show[] {
    return shows.filter(show => this.isShowFromToday(show));
  }

  /**
   * Find the most recent show from a list
   */
  static findNewestShow(shows: Show[]): Show | null {
    if (shows.length === 0) return null;

    try {
      return shows.reduce((newest, current) => {
        const newestTime = newest.date_start_time || '';
        const currentTime = current.date_start_time || '';
        return currentTime > newestTime ? current : newest;
      });
    } catch (error) {
      debugWarn('Error finding newest show:', error);
      return shows[0] || null;
    }
  }

  /**
   * Create a Set of show IDs for efficient comparison
   */
  static createShowIdSet(shows: Show[], idFields: (keyof Show)[]): Set<string> {
    return new Set(
      shows
        .map(show => this.generateShowId(show, idFields))
        .filter(id => id.length > 0)
    );
  }

  /**
   * Compare two show arrays and find new shows
   */
  static compareShows(
    currentShows: Show[],
    previousShows: Show[],
    idFields: (keyof Show)[]
  ): Show[] {
    if (!Array.isArray(currentShows) || !Array.isArray(previousShows)) {
      return [];
    }

    // If baseline is empty, all current shows are considered "new"
    if (previousShows.length === 0) {
      return [...currentShows];
    }

    const previousIds = this.createShowIdSet(previousShows, idFields);
    
    return currentShows.filter(show => {
      const showId = this.generateShowId(show, idFields);
      return showId.length > 0 && !previousIds.has(showId);
    });
  }
}

/**
 * Hook for detecting new shows by comparing current shows with a baseline
 * 
 * Features:
 * - Efficient show comparison using Set-based ID lookup
 * - Configurable show identification fields
 * - Today-only filtering for relevant new show detection
 * - Automatic newest show identification for animations
 * - Detection statistics and history tracking
 * - Memory-efficient baseline management
 * 
 * @param config - Configuration options for detection behavior
 * @returns Object with detection functions and utilities
 */
const DEFAULT_ID_FIELDS: (keyof Show)[] = ['show', 'date_start_time'];

export const useShowDetection = (config: ShowDetectionConfig = {}): UseShowDetectionReturn => {
  const {
    todayOnly = true,
    idFields = DEFAULT_ID_FIELDS,
    enableLogging = false,
  } = config;

  // Refs for persistent state across renders
  const baselineShows = useRef<Show[]>([]);
  const baselineInitialized = useRef<boolean>(false); // Track if baseline was ever set
  const detectionStats = useRef({
    totalDetections: 0,
    lastDetectionTime: null as number | null,
  });

  /**
   * Detect new shows by comparing current shows with baseline
   */
  const detectNewShows = useCallback((currentShows: Show[]): DetectionResult => {
    if (!Array.isArray(currentShows)) {
      if (enableLogging) {
        debugWarn('useShowDetection: Invalid currentShows array provided');
      }
      return {
        newShows: [],
        todayNewShows: [],
        newestShow: null,
        totalCount: 0,
        hasNewShows: false,
      };
    }

    try {
      // If baseline was never initialized, no shows are considered "new"
      if (!baselineInitialized.current) {
        return {
          newShows: [],
          todayNewShows: [],
          newestShow: null,
          totalCount: 0,
          hasNewShows: false,
        };
      }

      // Compare with baseline to find new shows
      const newShows = ShowDetectionUtils.compareShows(
        currentShows,
        baselineShows.current,
        idFields
      );

      // Filter to today's shows if configured
      const todayNewShows = todayOnly 
        ? ShowDetectionUtils.filterTodayShows(newShows)
        : newShows;

      // Find the newest show for animation purposes
      const newestShow = ShowDetectionUtils.findNewestShow(todayNewShows);

      const hasNewShows = todayNewShows.length > 0;

      // Update statistics if new shows were found
      if (hasNewShows) {
        detectionStats.current.totalDetections += 1;
        detectionStats.current.lastDetectionTime = Date.now();

        if (enableLogging) {
          debugLog('🎵 New shows detected:', {
            totalNew: newShows.length,
            todayNew: todayNewShows.length,
            newestShow: newestShow?.show || 'None',
          });
        }
      }

      return {
        newShows,
        todayNewShows,
        newestShow,
        totalCount: todayNewShows.length,
        hasNewShows,
      };
    } catch (error) {
      debugError('Error in show detection:', error);
      return {
        newShows: [],
        todayNewShows: [],
        newestShow: null,
        totalCount: 0,
        hasNewShows: false,
      };
    }
  }, [idFields, todayOnly, enableLogging]);

  /**
   * Update the baseline for future comparisons
   */
  const updateBaseline = useCallback((shows: Show[]) => {
    if (!Array.isArray(shows)) {
      if (enableLogging) {
        debugWarn('useShowDetection: Invalid shows array provided to updateBaseline');
      }
      return;
    }

    // Create a deep copy to prevent external mutations
    baselineShows.current = shows.map(show => ({ ...show }));
    baselineInitialized.current = true; // Mark baseline as initialized

    if (enableLogging) {
      debugLog('📊 Show detection baseline updated:', {
        showCount: shows.length,
        timestamp: new Date().toISOString(),
      });
    }
  }, [enableLogging]);

  /**
   * Get the current baseline shows
   */
  const getBaseline = useCallback((): Show[] => {
    return [...baselineShows.current];
  }, []);

  /**
   * Clear the baseline (useful for reset scenarios)
   */
  const clearBaseline = useCallback(() => {
    baselineShows.current = [];
    baselineInitialized.current = false; // Reset initialization flag
    
    if (enableLogging) {
      debugLog('🗑️ Show detection baseline cleared');
    }
  }, [enableLogging]);

  /**
   * Get statistics about detection history
   */
  const getStats = useCallback(() => {
    return {
      totalDetections: detectionStats.current.totalDetections,
      lastDetectionTime: detectionStats.current.lastDetectionTime,
      baselineSize: baselineShows.current.length,
    };
  }, []);

  return {
    detectNewShows,
    updateBaseline,
    getBaseline,
    clearBaseline,
    getStats,
  };
};

export default useShowDetection;
